const { ROOT_SHELL_COPY, STUB_SHELL_COPY } = require("./rootShellCopy");

const SHELL_BRAND_INPUTS = {
  fractolabs: {
    name: "FractoLabs",
    canonicalOrigin: "https://fractolabs.com",
    logoPath: "/images/logo.png",
    ogLocale: "en_US",
    legalName: "FractoLabs, operated by Sofia Aleksejeva",
    supportEmail: "support@fractolabs.com",
    favicon: "/favicon.ico",
    appleTouchIcon: "/apple-touch-fractolabs.png",
    manifest: "/manifest.webmanifest",
  },
  settrhq: {
    name: "SettrHQ",
    canonicalOrigin: "https://settrhq.com",
    logoPath: "/images/logo_settrhq.png",
    ogLocale: "en_US",
    legalName: "SettrHQ Pty Ltd",
    supportEmail: "support@settrhq.com",
    favicon: "/favicon_settrhq.ico",
    appleTouchIcon: "/apple-touch-settrhq.png",
    manifest: "/manifest-settrhq.webmanifest",
  },
};

function absoluteBrandUrl(canonicalOrigin, pathname) {
  if (!canonicalOrigin) return "";
  const origin = String(canonicalOrigin).replace(/\/$/, "");
  const path = pathname == null ? "" : String(pathname);
  if (path === "" || path === "/") return `${origin}/`;
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${withSlash}`;
}

function buildBrandJsonLd(brand) {
  if (!brand || !brand.canonicalOrigin || !brand.name) return "";
  const orgId = absoluteBrandUrl(brand.canonicalOrigin, "/#organization");
  const siteId = absoluteBrandUrl(brand.canonicalOrigin, "/#website");
  const organization = {
    "@type": "Organization",
    "@id": orgId,
    name: brand.name,
    legalName: brand.legalName,
    url: absoluteBrandUrl(brand.canonicalOrigin, "/"),
  };
  if (brand.appleTouchIcon) {
    organization.logo = absoluteBrandUrl(brand.canonicalOrigin, brand.appleTouchIcon);
  }
  if (brand.supportEmail) {
    organization.email = brand.supportEmail;
  }
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      organization,
      {
        "@type": "WebSite",
        "@id": siteId,
        url: absoluteBrandUrl(brand.canonicalOrigin, "/"),
        name: brand.name,
        publisher: { "@id": orgId },
      },
    ],
  };
  return JSON.stringify(graph).replace(/</g, "\\u003c");
}

function replacedRootCopy(name) {
  return {
    title: ROOT_SHELL_COPY.title.replaceAll("FractoLabs", name),
    description: ROOT_SHELL_COPY.description.replaceAll("FractoLabs", name),
    ogTitle: ROOT_SHELL_COPY.ogTitle.replaceAll("FractoLabs", name),
    ogDescription: ROOT_SHELL_COPY.ogDescription.replaceAll("FractoLabs", name),
    ogImageAlt: ROOT_SHELL_COPY.ogImageAlt.replaceAll("FractoLabs", name),
  };
}

function buildShellMeta(brand, options = {}) {
  if (brand === null) {
    return {
      ...STUB_SHELL_COPY,
      applicationName: "",
      siteName: "",
      robots: "noindex, nofollow",
      canonicalUrl: "",
      ogUrl: "",
      ogImage: "",
      ogLocale: "en_US",
      preserveCanonical: false,
      favicon: "",
      appleTouchIcon: "",
      manifest: "",
      jsonLd: "",
    };
  }

  const staging = options.staging === true;
  const canonicalUrl = absoluteBrandUrl(brand.canonicalOrigin, "/");
  return {
    ...replacedRootCopy(brand.name),
    applicationName: brand.name,
    siteName: brand.name,
    robots: staging ? "noindex, nofollow" : "index, follow",
    canonicalUrl,
    ogUrl: canonicalUrl,
    ogImage: brand.logo.default,
    ogLocale: brand.ogLocale,
    preserveCanonical: staging,
    favicon: brand.favicon,
    appleTouchIcon: brand.appleTouchIcon,
    manifest: brand.manifest,
    legalName: brand.legalName,
    supportEmail: brand.supportEmail,
    jsonLd: buildBrandJsonLd(brand),
  };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function metaName(name, content) {
  return `<meta name="${escapeHtml(name)}" content="${escapeHtml(content)}">`;
}

function metaProperty(property, content) {
  return `<meta property="${escapeHtml(property)}" content="${escapeHtml(content)}">`;
}

function renderShellHead(meta) {
  const tags = [];
  const add = (value, tag) => {
    if (value == null || value === "") return;
    tags.push(tag);
  };

  add(meta.description, metaName("description", meta.description));
  add(meta.applicationName, metaName("application-name", meta.applicationName));
  add(meta.robots, metaName("robots", meta.robots));
  add(meta.canonicalUrl, `<link rel="canonical" href="${escapeHtml(meta.canonicalUrl)}">`);
  add(meta.siteName, metaProperty("og:site_name", meta.siteName));
  add(meta.ogTitle, metaProperty("og:title", meta.ogTitle));
  add(meta.ogDescription, metaProperty("og:description", meta.ogDescription));
  tags.push(metaProperty("og:type", "website"));
  add(meta.ogUrl, metaProperty("og:url", meta.ogUrl));

  const imageUrl = absoluteBrandUrl(meta.canonicalUrl, meta.ogImage);
  add(imageUrl, metaProperty("og:image", imageUrl));
  add(meta.ogImageAlt, metaProperty("og:image:alt", meta.ogImageAlt));
  add(meta.ogLocale, metaProperty("og:locale", meta.ogLocale));

  if (meta.ogTitle != null && meta.ogTitle !== "") {
    tags.push(metaName("twitter:card", "summary_large_image"));
    tags.push(metaName("twitter:title", meta.ogTitle));
    add(meta.ogDescription, metaName("twitter:description", meta.ogDescription));
    add(imageUrl, metaName("twitter:image", imageUrl));
  }

  add(
    meta.appleTouchIcon,
    `<link rel="apple-touch-icon" href="${escapeHtml(meta.appleTouchIcon)}">`,
  );
  add(meta.manifest, `<link rel="manifest" href="${escapeHtml(meta.manifest)}">`);
  add(
    meta.jsonLd,
    `<script id="brand-jsonld" type="application/ld+json">${meta.jsonLd}</script>`,
  );

  return tags.join("\n");
}

function applyShellToHtml(html, meta) {
  const hasHead = html.includes("</head>");
  const hasTitle = /<title>[\s\S]*?<\/title>/.test(html);
  if (!hasHead || !hasTitle) {
    throw new Error("HTML is missing </head> or <title>");
  }

  const withTitle = html.replace(
    /<title>[\s\S]*?<\/title>/,
    `<title>${escapeHtml(meta.title)}</title>`,
  );
  const block = `<!-- brand-shell:start -->\n${renderShellHead(meta)}\n<!-- brand-shell:end -->`;
  const withBlock = withTitle.includes("<!-- brand-shell:start -->")
    ? withTitle.replace(/<!-- brand-shell:start -->[\s\S]*?<!-- brand-shell:end -->/, block)
    : withTitle.replace("</head>", `${block}\n</head>`);
  if (!meta.favicon) return withBlock;

  const iconTag = `<link rel="icon" href="${escapeHtml(meta.favicon)}">`;
  const iconRe = /<link\s+rel="icon"\s+href="[^"]*"\s*\/?>/;
  if (iconRe.test(withBlock)) {
    return withBlock.replace(iconRe, iconTag);
  }
  return withBlock.replace("<!-- brand-shell:start -->", `${iconTag}\n<!-- brand-shell:start -->`);
}

module.exports = {
  SHELL_BRAND_INPUTS,
  absoluteBrandUrl,
  buildBrandJsonLd,
  buildShellMeta,
  renderShellHead,
  applyShellToHtml,
};
